import { type Income } from "@/hooks/admin/shared/useIncome";
import { type BusinessExpense } from "@/hooks/useBusinessExpenses";
import { type BusinessIncome } from "@/hooks/useBusinessIncome";
import { type Expense } from "@/hooks/useExpenses";
import { type PaginatedBusinessIncome } from "@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeTable";
import { useState } from "react";

type Transaction = BusinessIncome | BusinessExpense | Income | Expense | PaginatedBusinessIncome;

interface UseTransactionModalsOptions<T extends Transaction> {
  onDelete: (id: string) => Promise<void> | void;
  withReceipt?: boolean;
}

const useTransactionModals = <T extends Transaction>({
  onDelete,
  withReceipt = false,
}: UseTransactionModalsOptions<T>) => {
  const [itemToEdit, setItemToEdit] = useState<T | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [receiptUrlToPreview, setReceiptUrlToPreview] = useState<string | null>(
    null,
  );

  const handleNew = () => {
    setItemToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: T) => {
    setItemToEdit(item);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setItemToEdit(null);
    setIsModalOpen(false);
  };

  const handleDelete = (item: T) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await onDelete(itemToDelete.id);
      setItemToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleViewReceipt = (receiptUrl: string) => {
    if (!withReceipt) return;
    setReceiptUrlToPreview(receiptUrl);
    setIsReceiptModalOpen(true);
  };

  const handleReceiptModalClose = () => {
    setReceiptUrlToPreview(null);
    setIsReceiptModalOpen(false);
  };

  return {
    itemToEdit,
    isModalOpen,
    itemToDelete,
    isDeleteDialogOpen,
    isReceiptModalOpen,
    receiptUrlToPreview,

    handleNew,
    handleEdit,
    handleModalClose,
    handleDelete,
    confirmDelete,
    handleViewReceipt: withReceipt ? handleViewReceipt : undefined,
    handleReceiptModalClose: withReceipt ? handleReceiptModalClose : undefined,

    setIsDeleteDialogOpen,
  };
};

export default useTransactionModals;
